import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../LanguageContext";
import { IMAGES } from "../content";
import { Reveal } from "./Reveal";

export const Gallery = () => {
  const { t } = useLang();
  const imgs = IMAGES.gallery;
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (active === null) return;
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((a) => (a + 1) % imgs.length);
      if (e.key === "ArrowLeft") setActive((a) => (a - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, imgs.length]);
  return (
    <section id="gallery" className="bg-ivory py-24 md:py-36" data-testid="gallery-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-16 md:mb-20 max-w-2xl">
          <Reveal>
            <p className="text-olive text-xs uppercase tracking-[0.35em] mb-6">{t.gallery.chapter}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-brown text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.gallery.title}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-brown/60 text-lg font-light">{t.gallery.lead}</p>
            <p className="mt-2 text-olive text-xs uppercase tracking-[0.25em]">{t.gallery.authentic}</p>
          </Reveal>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {imgs.map((item, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} y={40} className="mb-5 break-inside-avoid">
              <button
                onClick={() => setActive(i)}
                data-testid={`gallery-item-${i}`}
                className="group block w-full overflow-hidden rounded-xl bg-brown/5 shadow-lg shadow-brown/10"
              >
                <img
                  src={item.src}
                  alt={t.gallery.captions?.[i] ? `${t.gallery.captions[i]} — Restaurante São João` : `Restaurante São João — ${item.kind}`}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
              </button>
              {t.gallery.captions?.[i] && (
                <figcaption className="mt-3 text-brown/50 text-xs uppercase tracking-[0.2em]">{t.gallery.captions[i]}</figcaption>
              )}
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] bg-brown/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            onClick={() => setActive(null)}
            data-testid="lightbox"
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-6 right-6 text-ivory/80 hover:text-ivory transition-colors"
              data-testid="lightbox-close"
            >
              <X size={30} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setActive((a) => (a - 1 + imgs.length) % imgs.length); }}
              className="absolute left-4 md:left-8 text-ivory/70 hover:text-ivory transition-colors"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={40} />
            </button>
            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              src={imgs[active].src}
              alt={`Restaurante São João — ${imgs[active].kind}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-sm"
            />
            <button
              onClick={(e) => { e.stopPropagation(); setActive((a) => (a + 1) % imgs.length); }}
              className="absolute right-4 md:right-8 text-ivory/70 hover:text-ivory transition-colors"
              data-testid="lightbox-next"
            >
              <ChevronRight size={40} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
